import * as logger from 'loglevel'
import ApiService from '../Api/ApiService'

const ActionMessageType = 'ACTION'

const AblyLogLevel = 0 // 0 - no logging, 4 - verbose logging

class UserMessaging {
  constructor() {
    this.api = new ApiService()
    this.realtime = null
    this.realtimeInitPromise = null
    this.realtimeModulesPromise = null
    this.realtimeInitVersion = 0
    // Map<eventId, appSlug> used to prefix logs with [app.slug]
    this._eventAppSlugById = new Map()
  }

  registerEventAppSlug = (eventId, appSlug) => {
    if (!eventId) return
    const slug = (appSlug && typeof appSlug === 'string') ? appSlug : null
    if (!slug) return
    this._eventAppSlugById.set(String(eventId), slug)
  }

  registerEventAppSlugForChannel = (channelName, appSlug) => {
    const eventId = this._eventIdFromChannelName(channelName)
    if (!eventId) return
    this.registerEventAppSlug(eventId, appSlug)
  }

  _eventIdFromChannelName = (channelName) => {
    if (!channelName || typeof channelName !== 'string') return null
    // Typical format: anear:<clientId>:e:<eventId>:participants|actions|event|spectators|private:...
    const m = channelName.match(/:e:([^:]+):/)
    return m && m[1] ? m[1] : null
  }

  _logPrefixForChannelName = (channelName) => {
    const eventId = this._eventIdFromChannelName(channelName)
    if (!eventId) return '[unknown] '
    const slug = this._eventAppSlugById.get(String(eventId))
    return slug ? `[${slug}] ` : `[${eventId}] `
  }

  loadRealtimeModules = () => {
    if (!this.realtimeModulesPromise) {
      this.realtimeModulesPromise = Promise.all([
        import('ably/modular'),
        import('@ably/vcdiff-decoder')
      ]).then(([ablyModule, vcdiffModule]) => ({
        AblyModular: ablyModule,
        decode: vcdiffModule.decode || vcdiffModule.default
      })).catch((error) => {
        this.realtimeModulesPromise = null
        throw error
      })
    }

    return this.realtimeModulesPromise
  }

  initRealtime = async () => {
    if (this.realtime) return this.realtime
    if (this.realtimeInitPromise) return this.realtimeInitPromise

    const initVersion = ++this.realtimeInitVersion
    const initPromise = this.loadRealtimeModules()
      .then(({ AblyModular, decode }) => {
        if (this.realtime) return this.realtime
        if (initVersion !== this.realtimeInitVersion) return null

        const {
          BaseRealtime,
          FetchRequest,
          RealtimePresence,
          Rest,
          WebSocketTransport,
          XHRPolling
        } = AblyModular
        const clientOptions = {
          // Connection-scoped token: ANAPI mints wildcard caps for multi-event ABR.
          authCallback: async (_tokenParams, callback) => {
            try {
              const tokenRequest = await this.api.messagingAuth()
              callback(null, tokenRequest)
            } catch (error) {
              callback(error?.message || error || 'Unable to fetch messaging auth token', null)
            }
          },
          echoMessages: false,
          log: { level: AblyLogLevel },
          plugins: {
            vcdiff: decode,
            FetchRequest,
            RealtimePresence,
            Rest,
            WebSocketTransport,
            XHRPolling
          }
        }

        this.realtime = new BaseRealtime(clientOptions)
        logger.info('Ably Realtime initialized')
        return this.realtime
      })
      .finally(() => {
        if (this.realtimeInitPromise === initPromise) {
          this.realtimeInitPromise = null
        }
      })

    this.realtimeInitPromise = initPromise
    return initPromise
  }

  closeRealtime = () => {
    //
    // close out messaging for ALL events on device
    // initRealtime() must be invoked again to communicate
    // with Events
    //
    this.realtimeInitVersion += 1
    this.realtimeInitPromise = null

    if (this.realtime) {
      this.realtime.close()
      logger.info("Ably Realtime closed()")

      this.realtime = null
    }
  }

  getChannel = (channelName) => {
    if (!this.realtime) {
      throw new Error('Ably Realtime is not initialized')
    }

    const channelOptions = {
      delta: 'vcdiff',
    }

    return this.realtime.channels.get(channelName, channelOptions)
  }

  setChannelPresence = async (presenceChannel, id, requiresGeoLocation = false, urlContext = null) => {
    //
    // notifies the App that a participant is joining or a spectator is viewing
    // and optionally establishes geoLocation with the App
    // (for apps that require it and the user has consented)
    // id is the participantId or spectator userId
    //
    // IMPORTANT: Only obtains and sends geolocation when requiresGeoLocation is true.
    // This ensures geolocation is never inadvertently shared with events that don't require it.
    // 
    // CRITICAL: Channel must be attached before entering presence. Ably requires channels
    // to be in 'attached' state before presence operations can succeed.

    // Ensure channel is attached before entering presence
    if (presenceChannel.state !== 'attached') {
      await new Promise((resolve, reject) => {
        if (presenceChannel.state === 'attached') {
          resolve()
          return
        }
        
        const timeout = setTimeout(() => {
          presenceChannel.off()
          reject(new Error(`Timeout waiting for channel ${presenceChannel.name} to attach`))
        }, 10000) // 10 second timeout

        presenceChannel.once('attached', () => {
          clearTimeout(timeout)
          resolve()
        })

        presenceChannel.once('failed', (err) => {
          clearTimeout(timeout)
          presenceChannel.off()
          reject(new Error(`Channel ${presenceChannel.name} failed to attach: ${err}`))
        })

        // If channel is already attaching, we'll wait for it. Otherwise, attach it.
        if (presenceChannel.state === 'attaching') {
          // Already attaching, just wait
        } else {
          presenceChannel.attach((err) => {
            if (err) {
              clearTimeout(timeout)
              presenceChannel.off()
              reject(new Error(`Failed to attach channel ${presenceChannel.name}: ${err}`))
            }
            // If attach() succeeds synchronously, the 'attached' event will fire
          })
        }
      })
    }

    const context = { id }
    if (urlContext && typeof urlContext === 'object') {
      context.url = { ...urlContext }
    }

    // Only request geolocation if explicitly required
    // This is a defensive check to prevent geolocation from being sent when not needed
    if (requiresGeoLocation === true) {
      try {
        const geoLocation = await this.api.getPosition()
        context.geoLocation = geoLocation
      } catch (error) {
        logger.error(`Failed to get geolocation for presence: ${error.message}`)
        // If geolocation is required but unavailable, we still enter presence
        // without geolocation. The AppM or API will handle validation.
        // This prevents blocking the user from joining when geolocation fails.
      }
    }

    const pfx = this._logPrefixForChannelName(presenceChannel?.name)
    logger.info(
      `${pfx}setChannelPresence(${presenceChannel.name}, id: ${id}, requiresGeoLocation: ${requiresGeoLocation}, hasUrlContext: ${Boolean(context.url)})`
    )

    return presenceChannel.presence.enter(context)
  }

  updateChannelPresence = async (presenceChannel, id, requiresGeoLocation, urlContext = null) => {
    //
    // method is invoked when participant geoLocation changes, and only when required and
    // by the app and permission is granted by user
    //
    // IMPORTANT: Only obtains and sends geolocation when requiresGeoLocation is true.
    // This ensures geolocation is never inadvertently shared with events that don't require it.
    //
    const context = { id }
    if (urlContext && typeof urlContext === 'object') {
      context.url = { ...urlContext }
    }

    // Only request geolocation if explicitly required
    // This is a defensive check to prevent geolocation from being sent when not needed
    if (requiresGeoLocation === true) {
      try {
        const geoLocation = await this.api.getPosition()
        context.geoLocation = geoLocation
      } catch (error) {
        logger.error(`Failed to get geolocation for presence update: ${error.message}`)
        // If geolocation is required but unavailable, we still update presence
        // without geolocation. The AppM or API will handle validation.
        // This prevents blocking the user when geolocation fails.
      }
    }

    return presenceChannel.presence.update(context)
  }

  leaveChannelPresence = async (presenceChannel, id, options = {}) => {
    return presenceChannel.presence.leave({ id, ...options })
  }

  anearAuthHeaders = () => {
    return {
      ...this.api.defaultHeaderObject,
      Authorization: this.api.getAuthorizationBearer(),
    }
  }
  subscribePresenceEnterEvents = (channel, callback) => {
    if (!channel) {
      logger.error('subscribePresenceEnterEvents: channel is null or undefined');
      return;
    }

    ;(async () => {
      const pfx = this._logPrefixForChannelName(channel?.name)
      try {
        await channel.attach()
        channel.presence.subscribe('enter', callback);
        logger.info(`${pfx}subscribed to presence enter events on channel ${channel.name}`);
      } catch (err) {
        logger.error(`${pfx}attach failed on ${channel.name}: ${err}`);
      }
    })()
  }

  subscribeEventMessages = (channel, messageType, callback, withHistory=true) => {
    if (!channel) return

    ;(async () => {
      const pfx = this._logPrefixForChannelName(channel?.name)
      try {
        await channel.attach()
        // Wrap callback so receive logs are attributed to the correct app.
        const wrapped = async (msg) => {
          logger.debug(`${pfx}recv ${messageType} on ${channel.name}`)
          return await callback(msg)
        }
        channel.subscribe(messageType, wrapped)
        logger.info(`${pfx}subscribed to ${messageType} on channel ${channel.name}`)

        if (withHistory) await this.checkMessageHistory(channel, messageType, wrapped)
      } catch (err) {
        logger.error(`${pfx}attach failed: ${err}`)
      }
    })()
  }

  unSubscribeEventMessages = (channel, messageType) => {
    if (!channel) return

    const pfx = this._logPrefixForChannelName(channel?.name)
    logger.info(`${pfx}unsubscribe ${channel.name}: ${messageType}`)
    channel.unsubscribe(messageType)
  }

  checkMessageHistory = async (channel, messageType, callback) => {
    const pfx = this._logPrefixForChannelName(channel?.name)

    try {
      const resultPage = await channel.history({ untilAttach: true })
      const numItems = resultPage.items.length
      if (numItems > 0) {
        const messages = resultPage.items.filter(message => message.name === messageType)
        if (messages.length > 0) {
          logger.info(`${pfx}${messages.length} ${messageType} history events received in page.  processing most recent only`)
          await callback(messages[0])
        }
      }
    } catch (err) {
      logger.error(`${pfx}Unable to get channel history; err = ${err.message}`)
    }
  }

  publishAction = async (channel, message) => {
    try {
      await this.publishMessage(channel, ActionMessageType, message)
      const pfx = this._logPrefixForChannelName(channel?.name)
      logger.info(`${pfx}published Action: `, message)
    } catch(err) {
      logger.error(err.message)
    }
  }

  publishMessage = (channel, messageType, message) => {
    return channel.publish(messageType, message)
  }

  detachChannel = (channel) => {
    if (!channel || channel.state !== 'attached') {
      return Promise.resolve()
    }

    return channel.detach().then(() => {
      const pfx = this._logPrefixForChannelName(channel?.name)
      logger.info(`${pfx}${channel.name} has been detached`)
    })
  }
}

// Create a singleton instance of UserMessaging
const userMessagingInstance = new UserMessaging();

export default userMessagingInstance;
